import React from 'react'
import type { NextPage, GetServerSideProps, GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { ParsedUrlQuery } from 'querystring'
import {
  DonationBlockDataProps,
  FREQUENCY,
  MIN_DONATION_AMOUNT,
  SearchSelectOption,
  useCustomEventListener,
  type ActivityFeedProps,
  type BlockIdToProgramDesignations,
  type DonationProps,
  type ImpactProps,
  type LayoutProps,
  type ProgramDesignation,
  type ProgressMetricsProps,
  type ShareButtonProps,
  type SocialLinksProps,
} from '@classy/campaign-page-blocks'
import { nanoid } from 'nanoid'
import { CampaignPage, CampaignPageProps } from 'components/CampaignPage'
import { OpenGraphTags } from 'components/OpenGraphTags'
import { PAGE_TYPES, PageConfig } from 'models/pages'
import { getMockData, getMockPageConfig } from 'services/mock-data'
import {
  findCampaignPage,
  denormalizeLayoutWithBlocks,
  getCampaignData,
  getCampaignPagesData,
  getLegacyCampaignData,
  isCampaignInactive,
} from 'services/campaign'
import {
  addSocialLinksProps,
  buildSocialShareLinks,
  checkIsInstagramSharingEnabled,
  fetchSocialShareVideoConfiguration,
} from 'features/Block/social-links'
import { getOrganizationData, getOrganizationTags } from 'services/organization'
import { hasOrganizationTag } from 'utils/hasOrganizationTag'
import { domainMaskRedirect, getDomainFromRequest, getPrimaryDomain } from 'services/domain'
import { getSanitizedOrganizationChannels } from 'services/analytics'
import { getCheckoutQueryParamsToForward } from 'features/Block/donation/getCheckoutQueryParamsToForward'
import { getPageMetaDescription, getPageTitle } from 'utils/head'
import { isDomainValid } from 'utils/isDomainValid'
import { getFaviconLinks } from 'utils/getFaviconLinks'
import { __ENV } from 'utils/environment'
import { addImpactBlockProps, getProgramDesignationsFromImpactBlocks } from 'features/Block/impact'
import { getBlocksMap } from 'features/Block/utils/getBlocksMap'
import { addDonationBlockProps } from 'features/Block/donation/donation'
import { Block } from 'features/Block/block.model'
import { logger } from 'utils/logger'
import { HttpError } from 'utils/HttpError'
import {
  getCampaignProgramDesignations,
  getDesignationData,
  toProgramDesignation,
} from 'services/designations'
import { DesignationDto } from 'models/designations'
import { useIntelligentAskAmount } from 'hooks/useIntelligentAskAmount'
import { getFooter, addFooterBlockProps } from 'features/Block/footer/footer'
import { getPageHeader, addPageHeaderBlockProps } from 'features/Block/page-header/page-header'
import { addLayoutBlockProps } from 'features/Block/layout/layout'
import { addActivityFeedProps } from 'features/Block/activity-feed/activity-feed'
import { addProgressMetricsProps } from 'features/Block/progress-metrics/progress-metrics'
import { addShareButtonProps } from 'features/Block/share-button/share-button'
import { getOrganizationMetaFrapiIntegration } from 'services/organization/integrations'
import { toSearchSelectOptions } from 'features/Block/donation/useFetchProgramDesignations'
import { resolveCampaignLocale } from 'utils/resolveCampaignLocale'
import { trackCustomPageView } from 'services/analytics/events/customPageView'
import { trackPageViewEvent } from 'services/analytics/events/pageView'
import {
  useInternalAnalytics,
  useMParticleEventListener,
  useMParticleLogDonationFunnelStartViewed,
  useMParticleLogPageView,
  useMParticleLogExperimentViewed,
} from 'services/analytics-internal'
import { useClientHeadersData, ClientHeadersData } from 'hooks/useClientHeadersData'
import { useSsoMember } from 'hooks/useSsoMember'
import {
  trackAddToCartEvent,
  AnalyticsTrackAddToCartEvent,
} from 'services/analytics/events/addToCart'
import { EVENT } from 'models/event'
import { SsoSdk } from 'components/SsoSdk'
import { APIv2VideoStatus } from 'models/social-video'
import { addCustomEmbeddedCodeProps } from 'features/Block/custom-embedded-code/custom-embedded-code'
import { getOrgOptimizelyProjectId } from 'utils/optimizely/hooks/useOptimizelyPageLifecycle'
import OptimizelySDK from 'utils/optimizely/OptimizelySDK'
import { createOptimizelyUserAttributes } from 'utils/optimizely/createOptimizelyUserAttributes'
import { parseOptimizelyUrlOverrides } from 'utils/optimizely/parseOptimizelyUrlOverrides'
import { OPTIMIZELY_FEATURE_FLAGS } from 'models/optimizely'
interface DonationPageProps extends CampaignPageProps {
  isAnalyticsLoaded?: boolean
  hasCustomEmbeddedCode?: boolean
}

interface DonationPageQuery extends ParsedUrlQuery {
  mock?: string
  delay?: string
  campaignId: string
  pageId: string
}

const DonationPage: NextPage<DonationPageProps> = ({
  pageData,
  sharedBlocks,
  theme,
  pageConfig,
  isAnalyticsLoaded,
  __ENV,
}) => {
  const [isSsoSdkLoaded, setIsSsoSdkLoaded] = React.useState(false)
  const member = useSsoMember(isSsoSdkLoaded)
  const currentUrl = `https://${pageConfig.currentDomain}${pageConfig.pagePathname}`

  useIntelligentAskAmount(pageConfig.campaignId, pageConfig.intelligentAskConfig)

  /**
   * Fetching the client's headers data client side, instead of getServerSideProps
   * to prevent caching the IP address and currency.
   */
  const clientHeadersData: ClientHeadersData = useClientHeadersData()

  const { consentSynced } = useInternalAnalytics()

  /**
   * Analytics hooks (mParticle) executed on initial render:
   * - useMParticleLogPageView: sends a page-view event for the donation page.
   * - useMParticleLogDonationFunnelStartViewed: marks the start of the donation funnel.
   * - useMParticleLogExperimentViewed: logs Optimizely experiment exposures.
   * - useMParticleEventListener: attaches listeners to capture and forward user
   *   interactions after page load.
   */
  useMParticleLogPageView(pageConfig, !!member?.id)
  useMParticleLogDonationFunnelStartViewed(pageConfig, !!member?.id)
  useMParticleLogExperimentViewed(pageConfig, !!member?.id)
  useMParticleEventListener(pageConfig, !!member?.id)

  // Track whether page view events have already been fired to ensure they only fire once
  const analyticsEventsFired = React.useRef(false)

  /**
   * Fire page view events if airgap, isAnalyticsLoaded, clientHeadersData.cfConnectingIp are
   * true and only if member !== undefined. If member === undefined, then useSsoMember has not
   * finished checking whether a member is logged in or not.
   *
   * Uses a ref to ensure these events only fire once per page load.
   */
  React.useEffect(() => {
    if (
      !analyticsEventsFired.current &&
      (window.airgap || consentSynced) &&
      isAnalyticsLoaded &&
      !clientHeadersData.isLoading &&
      member !== undefined
    ) {
      // * GA4 & Tealium custom page view event
      trackCustomPageView(pageConfig, member)
      // * Default page view event used by all other analytics services
      trackPageViewEvent({ member, client_ip: clientHeadersData.cfConnectingIp })

      // Mark that the events have been fired
      analyticsEventsFired.current = true
    }
  }, [
    clientHeadersData.cfConnectingIp,
    clientHeadersData.isLoading,
    consentSynced,
    isAnalyticsLoaded,
    member,
    pageConfig,
  ])

  /**
   *   Custom Event Listener for Facebook Pixel and Meta CAPI trackAddToCartEvent analytics
   */
  const addToCartEventData = useCustomEventListener<AnalyticsTrackAddToCartEvent | null>(
    EVENT.ANALYTICS_TRACK_ADD_TO_CART,
    null,
  )

  React.useEffect(() => {
    if (addToCartEventData && addToCartEventData.newCartItem) {
      const { newCartItem, cartCount } = addToCartEventData

      trackAddToCartEvent({
        newCartItem,
        cartCount,
        clientIpAddress: clientHeadersData.cfConnectingIp,
        member,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToCartEventData])

  /**
   * Sticky CTA: lazy-load + viewport gating
   *
   * Why lazy-load:
   * - Reduce initial render work by not mounting effects/listeners
   *   until needed
   * - ssr: false to prevent SSR/hydration mismatches for
   *   client-only APIs (matchMedia, IntersectionObserver)
   */

  const LazyLoadedStickyCtaButton = dynamic(
    () => import('features/StickyCtaButton/StickyCtaButton').then((m) => m.StickyCtaButton),
    { ssr: false },
  )

  return (
    <>
      <Head>
        {getFaviconLinks(pageConfig.orgDomainMaskFavicon)}
        <title>{pageConfig.pageTitle}</title>
        <meta
          name="description"
          content={getPageMetaDescription(PAGE_TYPES.DONATION, pageConfig)}
        />
      </Head>
      <SsoSdk onReady={() => setIsSsoSdkLoaded(true)} pageConfig={pageConfig} __ENV={__ENV} />
      <OpenGraphTags
        facebookAppId={__ENV?.facebookAppId}
        facebookText={pageConfig.openGraphTagFacebookText || ''}
        facebookImageUrl={pageConfig.openGraphTagFacebookImageUrl || ''}
        orgName={pageConfig.orgName}
        currentUrl={currentUrl}
      />
      <CampaignPage
        __ENV={__ENV}
        theme={theme}
        pageData={pageData}
        sharedBlocks={sharedBlocks}
        pageConfig={pageConfig}
        member={member}
        clientHeadersData={clientHeadersData}
      />
      <LazyLoadedStickyCtaButton />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { locale, query, req, res, resolvedUrl } = context
  const { campaignId, delay = '0', mock, skipDomainMaskRedirect } = query as DonationPageQuery

  logger('trace', 'Start compiling donation page', {
    context: { campaignId, query, resolvedUrl },
  })

  try {
    if (mock) {
      const { theme, sharedBlocks, pageData } = await getMockData(
        Number(delay),
        campaignId,
        PAGE_TYPES.DONATION,
      )

      const { pageConfig } = await getMockPageConfig(campaignId, PAGE_TYPES.DONATION)

      return {
        props: {
          __ENV,
          theme,
          sharedBlocks,
          pageData,
          pageConfig,
        },
      }
    }

    /**
     * - Fetch the raw campaign data
     * - Fetch the donation page
     */
    const published = true
    const [campaign, campaignPages] = await Promise.all([
      getCampaignData({ campaignId, published }),
      getCampaignPagesData({ campaignId, published }),
    ])

    const [organization, domainMask, allOrgTags] = await Promise.all([
      getOrganizationData({
        organizationId: campaign.organization_id,
      }),
      getPrimaryDomain({
        organizationId: campaign.organization_id,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        campaignId,
      }),
    ])

    // Org tags
    const orgTagsData = allOrgTags.data
    const hasDataUseRestrictionTag = hasOrganizationTag(orgTagsData, 'data-use-restriction')
    const hasDisableMulticurrencyTag = hasOrganizationTag(orgTagsData, 'disable-multi-currency')
    const hasSpanishSpainLocalizationTag = hasOrganizationTag(
      orgTagsData,
      'localization-spanish_spain',
    )
    const hasProgramDesignationsSearchTag = hasOrganizationTag(orgTagsData, 'donation-block-search')
    const hasVideoSharingTag = hasOrganizationTag(orgTagsData, 'enable-video-sharing')
    const hasCustomEmbeddedCodeTag = hasOrganizationTag(orgTagsData, 'custom-embedded-code')
    const hasMsfOptimizelyTag = hasOrganizationTag(orgTagsData, 'msf-optimizely')

    const domain = getDomainFromRequest(req)
    const hostUrl = `https://${domain}`

    if (!isDomainValid(domain, domainMask)) {
      logger('error', new Error('Invalid domain or domain mask'), {
        context: { campaignId, query, domain, domainMask },
      })

      return {
        notFound: true,
      }
    }

    // When we implement Cloudflare caching on our pages, we will likely need to put in special rule
    // to specifically cache redirects (i.e. 308s) as Cloudflare does not cache certain redirects by
    // default: https://developers.cloudflare.com/cache/how-to/configure-cache-status-code
    if (!skipDomainMaskRedirect) {
      const redirect = domainMaskRedirect({
        currentDomain: domain,
        domainMask,
        url: resolvedUrl,
      })
      if (redirect) {
        return redirect
      }
    }

    if (isCampaignInactive(campaign)) {
      logger('warn', new Error('Campaign is inactive'), {
        context: {
          campaignId,
          externalUrl: campaign?.campaign_data?.external_url,
          organizationUrl: organization?.url,
        },
      })

      return {
        notFound: !campaign?.campaign_data?.external_url && !organization?.url,
        redirect: {
          permanent: false,
          destination: campaign?.campaign_data?.external_url || organization?.url,
        },
      }
    }

    const { theme, sharedBlocks } = campaignPages

    const donationPage = findCampaignPage(campaignPages, PAGE_TYPES.DONATION)
    if (!donationPage) {
      const error = new Error('Unable to find donation page in campaignPages data')
      logger('error', error, { context: { campaignId, campaignPages } })
      throw error
    }

    let donationPageData: Block
    try {
      donationPageData = denormalizeLayoutWithBlocks(donationPage)
    } catch (e) {
      const error = new Error('Unable to denormalize donation page data', { cause: e })
      logger('error', error, {
        context: { campaignId, blocks: donationPage.blocks, layout: donationPage.layout },
      })
      throw error
    }

    const blocksMap = getBlocksMap(donationPageData)

    const getCheckoutBaseUrl = () => {
      // check if user is bucketed in the checkout app 2025 treatment variant
      const isCheckoutApp2025Treatment =
        optimizelyContext[OPTIMIZELY_FEATURE_FLAGS.npo_checkout_app_2025]?.enabled &&
        optimizelyContext[OPTIMIZELY_FEATURE_FLAGS.npo_checkout_app_2025]?.variationKey ===
          'treatment'

      // however, at this time /givenow does not support carts under any circumstances
      const isCart = campaign?.checkout?.cart?.is_enabled ?? false

      const checkoutExperiencePath = !isCart && isCheckoutApp2025Treatment ? 'givenow' : 'checkout'

      const domain = domainMask ? hostUrl : `https://${process.env.CLASSY_STUDIO_DOMAIN}`

      return `${domain}/${checkoutExperiencePath}`
    }

    /**
     * Intelligent Ask Amount (IAA) setup
     *
     * TODO: CL-48068, clean up legacy IAA logic and merge onetimeEnabled
     * and recurringEnabled into a single boolean.
     */

    // If an org has a data use restriction tag, make sure IAA is set to false
    const dataUseRestriction = hasDataUseRestrictionTag

    let intelligentAskConfig: PageConfig['intelligentAskConfig']
    let isNewIaaEnabled = false
    if (campaign?.intelligent_asks) {
      /**
       * New Intelligent Ask Amount
       *
       * For backwards compatibility with legacy IAA, the is_enabled value is translated
       * to the onetimeEnabled and recurringEnabled values.
       */
      isNewIaaEnabled = !dataUseRestriction && (campaign?.intelligent_asks?.is_enabled || false)
      intelligentAskConfig = {
        onetimeEnabled: isNewIaaEnabled,
        recurringEnabled: isNewIaaEnabled,
      }
    } else {
      /**
       * Legacy IAA
       *
       * Legacy behavior relies on campaign_data.use_intelligent_ask_recurring and
       * campaign_data.use_intelligent_ask_onetime. If either are enabled, fetch IAA amounts.
       *
       * Note, if one is enabled and one disabled, the response from IAA would look like:
       * { suggested_donation_amount: number, suggested_recurring_amount: null }
       */
      intelligentAskConfig = {
        onetimeEnabled:
          !dataUseRestriction && (campaign?.campaign_data?.use_intelligent_ask_onetime || false),
        recurringEnabled:
          !dataUseRestriction && (campaign?.campaign_data?.use_intelligent_ask_recurring || false),
      }
    }

    const [organizationChannels, organizationMetaFrapiIntegration] = await Promise.all([
      getSanitizedOrganizationChannels({
        organizationId: campaign.organization_id,
      }),
      getOrganizationMetaFrapiIntegration({
        organizationId: campaign.organization_id,
      }),
    ])

    const analyticsServiceSettings = campaign?.modules?.analytics_service_settings ?? []

    /**
     * Optimizely for MSF (doctorswithoutborders) campaigns.
     * See `useOptimizelyPageLifecycle` hook for more details.
     */
    const orgOptimizelyProjectId = getOrgOptimizelyProjectId(
      hasMsfOptimizelyTag,
      analyticsServiceSettings,
    )

    const resolvedLocale = resolveCampaignLocale(
      locale,
      campaign.default_locale,
      hasSpanishSpainLocalizationTag,
    )

    const optimizelyUserId = context.req?.cookies?.['pgdid'] || nanoid()

    // only show easy cancel if there is a recurring frequency available on the donation block
    const isEligibleForEasyCancelExperiment = () => {
      // if IAA is enabled, there will always be one-time and monthly donation options
      if (campaign.intelligent_asks?.is_enabled) return true

      // assume only one donation block on page, or grab the first one
      const donationBlockProps = blocksMap['donation']?.[0]?.props as DonationBlockDataProps
      const hasRecurringFrequency = donationBlockProps?.donationFrequencies?.some(
        (frequency) => frequency.name !== FREQUENCY.ONE_TIME && frequency.isVisible,
      )
      return !!hasRecurringFrequency
    }

    const optimizelyUserAttributes = createOptimizelyUserAttributes({
      orgId: campaign.organization_id,
      campaignId,
      isCart: campaign?.checkout?.cart?.is_enabled ?? false,
      orgTags: allOrgTags.data,
      pageHasImpactBlock: !!blocksMap?.impact?.length,
      isEligibleForEasyCancel: isEligibleForEasyCancelExperiment(),
    })

    const urlOverrides = parseOptimizelyUrlOverrides(context.query, process.env.APP_ENV)

    const optimizelyContext = await OptimizelySDK.getDecisions(
      optimizelyUserId,
      optimizelyUserAttributes,
      urlOverrides,
    )

    /**
     * Whammy-specific runtime data (not injected into block data). All attributes MUST
     * have a value as any props returned by getServerSideProps will be serialized
     * and undefined values will throw an error.
     */
    const pageConfig: PageConfig = {
      analyticsServiceSettings,
      campaignId,
      campaignName: campaign.campaign_data.name,
      campaignCreatedAt: campaign.created_at,
      campaignLocale: resolvedLocale,
      campaignRawCurrencyCode: campaign.campaign_data.raw_currency_code,
      campaignRawGoal: campaign.campaign_data.raw_goal,
      defaultDesignationId: campaign.campaign_data.designation_id,
      checkoutBaseUrl: getCheckoutBaseUrl(),
      currentDomain: domain,
      customEmbeddedCodeDomain: process.env.CUSTOM_EMBEDDED_CODE_DOMAIN || '',
      forwardCheckoutQueryParams: getCheckoutQueryParamsToForward(query),
      isCartEnabled: campaign?.checkout?.cart?.is_enabled ?? false,
      isCustomEmbeddedCodeEnabled: hasCustomEmbeddedCodeTag,
      isPassportDisabled: hasDisableMulticurrencyTag,
      openGraphTagFacebookText: campaign?.modules.sharing?.facebook_text || '',
      openGraphTagFacebookImageUrl: campaign?.modules.sharing?.facebook_image_url || '',
      organizationChannels: organizationChannels || [],
      orgDomainMaskFavicon: domainMask?.favicon ?? '',
      orgId: campaign.organization_id,
      orgName: organization.name,
      orgOptimizelyProjectId: orgOptimizelyProjectId ?? '',
      pagePathname: req.url ?? '',
      pageTitle: getPageTitle(PAGE_TYPES.DONATION, campaign.campaign_data.name),
      intelligentAskConfig,
      optimizelyContext,
      blockCount: Object.keys(donationPage.blocks).length,
    }

    // * Adjust shared blocks

    const pageHeaderBlock = getPageHeader(sharedBlocks)
    if (pageHeaderBlock) {
      const { isCartEnabled = false } = pageConfig

      addPageHeaderBlockProps(pageHeaderBlock.props, organization, isCartEnabled)
    }

    const footerBlock = getFooter(sharedBlocks)
    if (footerBlock) {
      addFooterBlockProps(footerBlock.props, organization, campaign)
    }

    // * Adjust non-shared blocks

    if (blocksMap['activity-feed']) {
      // ! Assumes a campaign page will only have a single activity feed block
      const activityFeed = blocksMap['activity-feed'][0]
      addActivityFeedProps(
        // Force narrowing type (AnyBlockProps -> ActivityFeedProps)
        activityFeed.props as ActivityFeedProps,
        campaignId,
        campaign.organization_id,
        campaign?.modules?.donation_matching_plan,
      )
    }

    // TODO added per CL-39568, clean up with CL-40328
    blocksMap.layout?.forEach((layoutBlock) => {
      addLayoutBlockProps(layoutBlock.props as LayoutProps, campaignPages.schema_version)
    })

    if (blocksMap['progress-metrics']) {
      blocksMap['progress-metrics'].forEach((progressMetricsBlock) => {
        addProgressMetricsProps(
          progressMetricsBlock.props as ProgressMetricsProps,
          campaign.campaign_data.raw_currency_code,
          resolvedLocale,
        )
      })
    }

    if (blocksMap['social-links'] || blocksMap['share-button']) {
      let videoData: APIv2VideoStatus | undefined
      let videoPreviewOptions: SocialLinksProps['videoPreview']

      if (hasVideoSharingTag) {
        const { videoData: fetchedVideoData, videoPreviewOptions: fetchedVideoPreviewOptions } =
          await fetchSocialShareVideoConfiguration(campaign)

        videoData = fetchedVideoData
        videoPreviewOptions = fetchedVideoPreviewOptions
      }

      // Build social share links with the video data
      const socialShareLinks = buildSocialShareLinks(campaign, domainMask?.value, videoData)

      const isInstagramSharingEnabled = checkIsInstagramSharingEnabled(
        organizationMetaFrapiIntegration,
        campaign.modules.campaign_channel || [],
      )

      blocksMap['social-links']?.forEach((socialLinksBlock) => {
        addSocialLinksProps(
          socialLinksBlock.props as SocialLinksProps,
          socialShareLinks,
          isInstagramSharingEnabled,
          videoPreviewOptions,
        )
      })

      blocksMap['share-button']?.forEach((shareButtonBlock) => {
        addShareButtonProps(
          shareButtonBlock.props as ShareButtonProps,
          socialShareLinks,
          isInstagramSharingEnabled,
          videoPreviewOptions,
        )
      })
    }

    let defaultProgramDesignationId: number | null = null
    let defaultProgramDesignation: ProgramDesignation = {} as ProgramDesignation
    let programDesignations: BlockIdToProgramDesignations = {}
    try {
      /**
       * Under Settings > Program Designations, the value for sort_designation_by is saved at the
       * legacy APIv2 /campaigns/{id} end point.
       *
       * https://docs.classy-test.org/apiv2/#tag/Campaign/operation/fetchCampaign
       *
       * Then fetch the campaign's sorted program designation group by sort_designation_by.
       * Assumes designation_id is the campaign's default program designation.
       */
      const { designation_id, sort_designation_by } = await getLegacyCampaignData({
        campaignId,
      })

      defaultProgramDesignationId = designation_id

      const designationData = await getDesignationData({
        designationId: designation_id,
      })

      const designationDataJson: DesignationDto = await designationData.json()
      defaultProgramDesignation = toProgramDesignation(designationDataJson)

      if (blocksMap.impact) {
        // Precompiling all designations across all impact blocks
        programDesignations = await getProgramDesignationsFromImpactBlocks(
          blocksMap.impact,
          sort_designation_by,
          defaultProgramDesignation,
          campaignId,
        )
      }
    } catch (e) {
      const error = new Error('Unable to apply program designations to blocks', { cause: e })
      logger('error', error, {
        context: {
          defaultProgramDesignationId,
          defaultProgramDesignation,
          campaignId,
          orgId: pageConfig.orgId,
        },
      })

      throw error
    }

    blocksMap.impact?.forEach((impactBlock) => {
      addImpactBlockProps(
        // Force narrowing type (AnyBlockProps -> ImpactProps)
        impactBlock.props as ImpactProps,
        campaignId,
        pageConfig.checkoutBaseUrl || '',
        pageConfig.forwardCheckoutQueryParams || {},
        programDesignations,
        campaign.checkout?.cart?.is_enabled || false,
        campaign.campaign_data.minimum_donation_amount || MIN_DONATION_AMOUNT,
        campaign.organization_id,
        campaign.campaign_data.raw_currency_code || 'USD',
        resolvedLocale,
      )
    })

    let initialCampaignProgramDesignations: SearchSelectOption[] = []
    if (hasProgramDesignationsSearchTag && blocksMap.donation && pageConfig.isCartEnabled) {
      const fetchDesignations = blocksMap.donation?.some(
        // Force narrowing type (AnyBlockProps -> DonationProps)
        (donationBlock) => (donationBlock.props as DonationProps)?.showProgramDesignationsSearch,
      )

      if (fetchDesignations) {
        const { designations } = await getCampaignProgramDesignations({ campaignId })
        initialCampaignProgramDesignations = toSearchSelectOptions(designations)
      }
    }

    /**
     * Accumulates experiment-specific CSS class names for the Donation block.
     * These are later joined into a single className and passed to addDonationBlockProps
     * to trigger experiment-specific styling within the block.
     */

    const expClassNames: string[] = []

    blocksMap.donation?.forEach((donationBlock) => {
      /**
       * FYI, IAA config is not block data, it is external block data, which comes from
       * campaign.campaign_data, and passed to the client via PageConfig.
       */
      addDonationBlockProps(
        // Force narrowing type (AnyBlockProps -> DonationProps)
        donationBlock.props as DonationProps,
        query,
        campaignId,
        campaign.organization_id,
        campaign.campaign_data.dcf_enabled,
        defaultProgramDesignation,
        pageConfig.forwardCheckoutQueryParams || {},
        isNewIaaEnabled
          ? MIN_DONATION_AMOUNT
          : campaign?.campaign_data?.minimum_donation_amount || MIN_DONATION_AMOUNT,
        campaign?.checkout?.cart?.is_enabled ?? false,
        pageConfig.checkoutBaseUrl || '',
        campaign.campaign_data.raw_currency_code,
        initialCampaignProgramDesignations,
        pageConfig.intelligentAskConfig,
        resolvedLocale,
        !campaign.campaign_data.hide_dedications,
        expClassNames.join(' '),
      )
    })

    if (hasCustomEmbeddedCodeTag) {
      logger('info', 'Processing CustomEmbeddedCode block(s) on the donation page', {
        context: { campaignId },
      })

      for (const customEmbeddedCodeBlock of blocksMap['custom-embedded-code'] || []) {
        addCustomEmbeddedCodeProps(
          customEmbeddedCodeBlock.props,
          customEmbeddedCodeBlock.id,
          campaignId,
          pageConfig.customEmbeddedCodeDomain,
        )
      }
    }

    // * Set CloudFlare cache-tag
    res.setHeader(
      'Cache-Tag',
      `campaign-${campaignId}-${PAGE_TYPES.DONATION},campaign-${campaignId},organization-${campaign.organization_id}`,
    )

    logger('trace', 'Successfully compiled donation page', {
      context: { campaignId },
    })

    return {
      props: {
        __ENV,
        theme,
        sharedBlocks,
        pageData: donationPageData,
        pageConfig,
        ...(await serverSideTranslations(pageConfig.campaignLocale)),
      },
    }
  } catch (e) {
    const isHttpError = e instanceof HttpError
    logger('error', new Error('Unable to compile donation page', { cause: e }), {
      context: {
        campaignId,
        query,
        resolvedUrl,
        statusCode: isHttpError ? e.statusCode : undefined,
      },
    })

    // Check if it's our HttpError with 404 status - use Next.js built-in 404 handling
    if (isHttpError && e.statusCode === 404) {
      return {
        notFound: true,
      }
    }

    // Re-throw the error to let Next.js handle it properly
    throw e
  }
}

export default DonationPage
